import React from 'react';
import { Bubble, Switch, TableColumn } from 'tea-component';
import LinkJump from '@gp/components/LinkJump';
import { getBasePath } from '@/utils/utils';
import {
  generateMockId,
  getFileAndSuffixByFileName,
  openTabPage,
  reportBtnOperationLog,
} from '@gp/utils/common';
import { CommonColumnBuilders } from '@gp/utils/columns';
import { formatMilestoneDateByStatus } from '@gp/utils/milestone';
import { buildProjectSelectColumn, buildProjectColumn } from '../../../../utils/columns';
import { getIsDisabled } from '../../../../utils';
import { MilestoneDateStatus } from '@gp/enums/milestone';
import { TABLE_WIDTH_IN_SMALL_SCREEN, SMALL_WIDTH_COLUMN_WIDTH } from '../../../../constants';
import { DPS_REPORT_MODULE_KEY } from '../../../constants';
import type { IRecord, IMaterialVO } from '@gp/types/global';
import type { IDPSReportCalendarProjectSimpleVO } from '@gp/types/dpsReport/common';
import type { IDPSReportMemoPendingApprovalVO } from '@gp/types/dpsReport/v3';

export const CARD_TITLE = 'Memo Pending Approval';

// Comment列的宽度（没有GR Date列）
const COMMENTS_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN1 = 630;
const COMMENTS_WIDTH_COLUMN_WIDTH1 = `${
  (COMMENTS_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN1 / TABLE_WIDTH_IN_SMALL_SCREEN) * 100
}%`;

// Comment列的宽度（包含GR Date列）
const COMMENTS_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN2 = 500;
const COMMENTS_WIDTH_COLUMN_WIDTH2 = `${
  (COMMENTS_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN2 / TABLE_WIDTH_IN_SMALL_SCREEN) * 100
}%`;

const MEMO_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN = 180;
const MEMO_WIDTH_COLUMN_WIDTH = `${
  (MEMO_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN / TABLE_WIDTH_IN_SMALL_SCREEN) * 100
}%`;

const GR_DATE_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN = 230;
const GR_DATE_WIDTH_COLUMN_WIDTH = `${
  (GR_DATE_WIDTH_COLUMN_WIDTH_IN_SMALL_SCREEN / TABLE_WIDTH_IN_SMALL_SCREEN) * 100
}%`;

const ProjectColumn = buildProjectColumn<IDPSReportMemoPendingApprovalVO>({
  header: 'Project',
  width: SMALL_WIDTH_COLUMN_WIDTH,
});

const memoGRDateColumn = CommonColumnBuilders.buildPureTextColumn({
  key: 'date',
  header: 'Review Date',
  width: GR_DATE_WIDTH_COLUMN_WIDTH,
  formatter: (cvm: IDPSReportMemoPendingApprovalVO): string => {
    const { date, date_confirm: dateConfirm } = cvm || {};
    return formatMilestoneDateByStatus(date, dateConfirm) || '-';
  },
});

const getCommentColumnWidth = (showGRDate?: boolean): string => {
  return showGRDate ? COMMENTS_WIDTH_COLUMN_WIDTH2 : COMMENTS_WIDTH_COLUMN_WIDTH1;
};

export const buildRecord = (): IDPSReportMemoPendingApprovalVO => ({
  mock_id: generateMockId(),
  project_id: '',
  project: '',
  platform: [],
  image_name: '',
  cover: '',
  priority: '',
  milestone_id: '',
  comment: '',
  material: [],
  memo_open: false,
  date: '',
  date_confirm: MilestoneDateStatus.Confirm,
});

export const getColumnsEditable = (props: {
  showGRDate?: boolean;
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  bubbleVisiblesMapping?: IRecord<boolean | undefined>;
  onProjectChange?: (cvm: IDPSReportMemoPendingApprovalVO, payload: string) => void;
  onCommentChange?: (cvm: IDPSReportMemoPendingApprovalVO, payload: string) => void;
  onMemoChange?: (cvm: IDPSReportMemoPendingApprovalVO, payload: boolean) => void;
}): TableColumn<IDPSReportMemoPendingApprovalVO>[] => {
  const {
    showGRDate = false,
    projects,
    bubbleVisiblesMapping,
    onProjectChange,
    onCommentChange,
    onMemoChange,
  } = props || {};
  const projectsOptions = projects?.map((project) => {
    const { project_id: projectId, project: projectName } = project || {};
    return { text: projectName || '', value: projectId || '' };
  });

  const columns: TableColumn<IDPSReportMemoPendingApprovalVO>[] = [
    buildProjectSelectColumn({
      width: SMALL_WIDTH_COLUMN_WIDTH,
      showTextWhenSelected: false,
      options: projectsOptions || [],
      onChange: (payload, cvm) => {
        onProjectChange?.(cvm, payload);
      },
    }),
    CommonColumnBuilders.buildEditorColumn<IDPSReportMemoPendingApprovalVO>({
      key: 'comment',
      header: 'Comments ',
      width: getCommentColumnWidth(showGRDate),
      placeholder: 'Please input Comments',
      minHeight: 120,
      getIsDisabled: (cvm) => getIsDisabled(cvm),
      onChange: (cvm, key, payload) => {
        onCommentChange?.(cvm, payload);
      },
    }),
    // {
    //   key: 'memo',
    //   header: 'Link to Memo File',
    //   width: MEMO_WIDTH_COLUMN_WIDTH,
    //   align: 'left',
    //   render: (cvm) => {
    //     const {
    //       project_id: projectId,
    //       milestone_id: milestoneId,
    //       material: materials,
    //       memo_open: memoOpen,
    //     } = cvm || {};
    //     const hasProjectMaterials =
    //       !!milestoneId && Array.isArray(materials) && materials.length > 0;
    //     const disabled = !projectId || !hasProjectMaterials;
    //     const bubbleVisible = !projectId ? false : bubbleVisiblesMapping?.[projectId];

    //     return (
    //       <Bubble
    //         key={`${projectId}_${bubbleVisible}`}
    //         dark
    //         arrowPointAtCenter
    //         overlayClassName="dbg-bubble-max-width"
    //         content="No Memo File found in recent GR"
    //         visible={bubbleVisible}
    //       >
    //         <Switch
    //           content=""
    //           disabled={disabled}
    //           value={memoOpen}
    //           onChange={(payload) => {
    //             onMemoChange?.(cvm, payload);
    //           }}
    //         />
    //       </Bubble>
    //     );
    //   },
    // },
  ];
  if (showGRDate) {
    columns.push(memoGRDateColumn);
  }
  return columns;
};

const getPreviewerUrl = (params?: {
  projectId?: string;
  reportId?: string;
  path?: string;
  name?: string;
  format?: string;
}): string => {
  const { protocol, host } = window.location;
  const basePath = getBasePath();
  const { projectId, reportId, path, name, format } = params || {};
  const urlSearchParamsTmp = new URLSearchParams({
    project_id: projectId || '',
    report_id: reportId || '',
    report_module_key: DPS_REPORT_MODULE_KEY.MEMO,
    // 预览类型，用于区分进入对应的预览页
    previewer_type: 'dps_report_memo_materials',
    // 预览文档的COS存储path
    doc_key: path || '',
    // 预览文档的名称
    doc_name: name || '',
    // 预览文档的文件后缀
    doc_suffix: format || '',
  });
  return `${protocol}//${host}${basePath}/g-pop/DocPreviewer?${urlSearchParamsTmp.toString()}`;
};

const renderMemoLinker = (props: {
  projectId?: string;
  reportId?: string;
  materials?: IMaterialVO[] | null;
}) => {
  const { projectId, reportId, materials } = props || {};
  const { path, name: fullName } = materials?.[0] || {};
  const { name, format } = getFileAndSuffixByFileName(fullName);
  const url = getPreviewerUrl({
    projectId,
    reportId,
    path,
    name,
    format,
  });

  return (
    <LinkJump
      isStopPropagation
      isPreventDefault
      url={url}
      onClick={() => {
        reportBtnOperationLog('WORKSPACE_DPS_WEEKLY_REPORT_MEMO_SUBMIT');
        openTabPage(url);
      }}
    >
      <span className="dbg-initial-line-height dbg-cursor-pointer dbg-text-highlight">
        Memo on GPOP
      </span>
    </LinkJump>
  );
};

export const getColumnsReadonly = (params?: {
  showGRDate?: boolean;
  reportId?: string;
}): TableColumn<IDPSReportMemoPendingApprovalVO>[] => {
  const { showGRDate = false, reportId } = params || {};
  const columns: TableColumn<IDPSReportMemoPendingApprovalVO>[] = [
    {
      ...ProjectColumn,
      render: (cvm, ...restParams) =>
        // @ts-ignore
        ProjectColumn?.render(cvm, ...restParams),
    },
    CommonColumnBuilders.buildRichTextColumn({
      key: 'comment',
      header: 'Comments',
      width: getCommentColumnWidth(showGRDate),
    }),
    // {
    //   key: 'memo',
    //   header: 'More Detail',
    //   width: MEMO_WIDTH_COLUMN_WIDTH,
    //   render: (cvm) => {
    //     const {
    //       project_id: projectId,
    //       milestone_id: milestoneId,
    //       memo_open: memoOpen,
    //       material: materials,
    //     } = cvm;
    //     const hasMemoMaterials =
    //       !!memoOpen && !!milestoneId && Array.isArray(materials) && materials.length > 0;

    //     if (!hasMemoMaterials) return <span className="dbg-initial-line-height">No Memo</span>;

    //     return renderMemoLinker({ projectId, reportId, materials });
    //   },
    // },
  ];
  if (showGRDate) {
    columns.push(memoGRDateColumn);
  }
  return columns;
};
